import { useState, useEffect, useMemo } from "react";
import { Button, Modal, Input, Spin, message } from "antd";
import { Plus, X, Image as ImageIcon } from "react-feather";
import styles from "./stylesUpcomingTabs.module.css";
// Import AddNewIpPopup styles for consistent modal styling
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
// Import API functions
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import SortingTable from "../SortingTable/SortingTable";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
  getListEntityBaseName,
  toListTranslationEditorData,
} from "../Translations/listTranslationData";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";

const UpcomingTabs = () => {
  const { canEdit } = useSportsbookFm();
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  // Rename state
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const renameTranslationData = useMemo(
    () =>
      toListTranslationEditorData(
        renameTarget,
        renameTarget?.id,
        renameTarget?.ename ?? ""
      ),
    [renameTarget]
  );
  const renameInitialName = translationsEnabled
    ? getListEntityBaseName(renameTarget, renameTarget?.ename ?? "")
    : (renameTarget?.ename ?? "");

  // Get token and create API instance
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [upcomingTabs, setUpcomingTabs] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // API Functions
  const fetchUpcomingTabs = async () => {
    try {
      setLoading(true);
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_UPCOMING_TABS
      );

      if (response.data.success) {
        const upcomingTabsData = response.data.data.items.map((item) => ({
          id: item?.entity_id || "",
          ename: item?.display_name || "",
          order: item?.display_order?.toString() || "",
          is_active: item?.is_active || "",
          type: item?.item_type || "",
          is_pinned: item?.is_pinned || "",
          sport: item?.sport_name || "",
          icon: item?.image_link || "",
          league_id: item?.league_id || "",
          sport_id: item?.sport_id || "",
          event_id: item?.event_id || "",
          translations: item?.translations || null,
        }));

        setUpcomingTabs(upcomingTabsData);
      }
    } catch (error) {
      console.error("Error fetching top upcomingTabs:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setIsAddModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setSearchValue("");
    setSelectedItem(null);
    setSearchOptions([]);
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const response = await PrivateAdminPanelInstance.get(
            API_ROUTES.AUTOCOMPLETE_UPCOMING_TABS,
            {
              params: {
                search_term: query,
              },
            }
          );

          if (response.data.success) {
            const searchData = response.data.data.map((league) => ({
              entity_id: league.entity_id,
              item_name: league.item_name,
              item_type: league.item_type,
              sport_id: league.sport_id,
              sport_name: league.sport_name,
              logo: league.logo_link,
              country_name: league.country_name,
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error("Error searching UpcomingTabs:", error);
          message.error(
            getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
          );
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, []);

  useEffect(() => {
    fetchUpcomingTabs();
  }, []);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  const handleAddItemConfirm = async () => {
    if (!selectedItem) return;
    setAddLoading(true);
    try {  
      const itemId = selectedItem.entity_id.replace(/^(SPORT|LEAGUE|EVENT)#/, "");
      
      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_UPCOMING_TAB,
        {
          item_type: selectedItem.item_type,
          item_id: itemId,
          display_name: selectedItem.item_name,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      setIsAddModalVisible(false);
      setSearchValue("");
      setSelectedItem(null);
      setSearchOptions([]);

      // Refresh the UpcomingTabs list
      await fetchUpcomingTabs();
    } catch (error) {
      console.error("Error adding item:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setAddLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    setIsTableLoading(true);

    const payload = {
      entity_id: record.id,
      is_active: !record.is_active,
    };
    
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.SET_UPCOMING_TAB_STATUS,
        payload,
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setUpcomingTabs((prevData) =>
        prevData.map((item) =>
          item.id === record.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );
    } catch (error) {
      console.error("Error toggling item status:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const removeItem = async (record) => {
    setIsTableLoading(true);
    
    const payload = {
      entity_id: record.id,
    };

    try {
      const response = await PrivateAdminPanelInstance.delete(
        API_ROUTES.DELETE_UPCOMING_TAB,
        { data: payload }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setUpcomingTabs((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
    } catch (error) {
      console.error("Error deleting item:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const changeOrder = async (reorderedItems) => {
    setIsTableLoading(true);
    try {
      // Update local state first for immediate UI feedback
      setUpcomingTabs(reorderedItems);

      // Send API request to reorder
      const orderedIds = reorderedItems.map((item) => item.id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.ORDER_UPCOMING_TABS,
        {
          ordered_ids: orderedIds,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
    } catch (error) {
      console.error("Error reordering UpcomingTabs:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      // Revert to original state on error
      fetchUpcomingTabs();
    } finally {
      setIsTableLoading(false);
    }
  };

  // Rename handlers
  const handleRenameClick = (item) => {
    setRenameTarget(item);
    setIsRenameModalVisible(true);
  };

  const handleRenameModalClose = () => {
    setIsRenameModalVisible(false);
    setRenameTarget(null);
  };

  const applySavedUpcomingTranslations = ({ translation_key, translations }) => {
    if (!renameTarget) return;
    setUpcomingTabs((prevData) =>
      prevData.map((item) =>
        item.id === renameTarget.id
          ? applyListEntityTranslations(
              item,
              translations,
              "ename",
              translation_key
            )
          : item
      )
    );
  };

  const handleRenameConfirm = async (name, translationChanges) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.RENAME_UPCOMING_TAB,
        {
          entity_id: renameTarget.id,
          display_name: name,
          ...(translationChanges
            ? {
                translation_key: translationChanges.translation_key,
                translations: translationChanges.translations,
              }
            : {}),
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.ITEM_RENAMED_SUCCESSFULLY)
      );

      setUpcomingTabs((prevData) =>
        prevData.map((item) => {
          if (item.id !== renameTarget.id) return item;
          const renamed = applyListEntityRename(
            item,
            name,
            translationsEnabled,
            "ename"
          );
          if (!translationChanges) return renamed;
          return applyListEntityTranslations(
            renamed,
            translationChanges.translations,
            "ename",
            translationChanges.translation_key
          );
        })
      );

      handleRenameModalClose();
    } catch (error) {
      console.error("Error renaming item:", error);
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_ITEM));
    } finally {
      setRenameLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{AppStrings.UPCOMING_TABS}</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
            className={styles.addButton}
          >
            {AppStrings.ADD_SPORT}
          </Button>
        )}
      </div>

      <div className={styles.tableContainer}>
        <SortingTable
          columns={[
            "",
            "#Order",
            "Name",
            "Type",
            "Status",
            "Action",
          ]}
          data={upcomingTabs}
          setData={setUpcomingTabs}
          toggleStatus={toggleStatus}
          removeItem={removeItem}
          onRename={handleRenameClick}
          changeOrder={changeOrder}
          isLoading={isTableLoading}
          canEdit={canEdit}
        />
      </div>

      <Modal
        closable={false}
        open={isAddModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.UPCOMING_TABS}</h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                placeholder="Search sports"
                value={searchValue}
                onChange={handleSearchChange}
              />
              {searchValue ? (
                <div>
                  {searchLoading ? (
                    <div className="center">
                      <Spin size="small" />
                    </div>
                  ) : (
                    searchOptions?.map((option) => (
                      <div
                        key={option.entity_id}
                        className={`${styles.leagueCard} ${
                          selectedItem?.entity_id === option.entity_id
                            ? styles.selected
                            : ""
                        }`}
                        onClick={() => handleItemSelect(option)}
                      >
                        <div className={styles.leagueLogo}>
                          {option?.logo ? (
                            <img
                              src={option?.logo}
                              alt="Logo"
                              className={styles.logoEmoji}
                            />
                          ) : (
                            <ImageIcon size={18} />
                          )}
                        </div>
                        <div className={styles.leagueInfo}>
                          <span className={styles.leagueName}>
                            {option.item_name}
                          </span>
                        </div>
                        <div className={styles.typeTag}>
                          <span className={styles.typeText}>
                            {option.item_type}
                          </span>
                        </div>
                        {option.sport_name && (
                          <div className={styles.sportTag}>
                            <span className={styles.sportText}>
                              {option.sport_name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {!searchLoading &&
                    searchOptions.length === 0 &&
                    searchValue && (
                      <div className="empty-mini">
                        No results found
                      </div>
                    )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={handleModalClose}
              disabled={addLoading}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${
                !selectedItem || addLoading ? "disabled" : ""
              }`}
              disabled={!selectedItem || addLoading}
              onClick={handleAddItemConfirm}
            >
              {addLoading ? AppStrings.ADDING : AppStrings.ADD_SPORT}
            </button>
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={isRenameModalVisible}
        title={AppStrings.RENAME_UPCOMING_TAB}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameInitialName}
        entityKey={renameTarget?.translations?.key ?? renameTarget?.id}
        initialTranslationData={renameTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={renameLoading}
        onSave={handleRenameConfirm}
        onTranslationsSaved={applySavedUpcomingTranslations}
        onClose={handleRenameModalClose}
      />
    </div>
  );
};

export default UpcomingTabs;
